import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Collapse, Descriptions, Spin, Button, Empty, Tag} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { Block, Report } from "notiflix";
import moment from "moment";

import Navbar from "../../../atoms/navbar";
import Footer from "../../../atoms/footer";

import ApprovalActions from "../../../atoms/approval-actions";

import { requestToken } from "../../../auth/AuthServices";
import HeaderFarms from "../../../utils/header_farms";
import API from "../../../services";

import "./styles.css";
import PdfViewer from "../../../atoms/pdf-viewer";
import ApprovalInfo from "../../../atoms/approval-info";

const { Panel } = Collapse;

const statusMeta = {
    "Waiting Approval": {
        color: "gold",
        label: "Waiting Approval",
    },
    Approved: {
        color: "blue",
        label: "Approved",
    },
    Reject: {
        color: "red",
        label: "Rejected",
    },
    Cancel: {
        color: "default",
        label: "Canceled",
    },
};

const ViewDASTR = () => {

    const { recordId } = useParams();
    const navigate = useNavigate();

    const [documentData, setDocumentData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeKeys, setActiveKeys] = useState(['1', '2', '3']);

    const statusInfo = useMemo(() => {

        if (!documentData?.approval_process_id?.data) return null;
        const status = documentData.approval_process_id.data.status;
        return {
            status,
            ...(statusMeta[status] || { color: "default", label: status || "-" }),
        };
    }, [documentData]);

    const fetchDetail = async () => {
        Block.hourglass(".dastr-view__body");
        try {
            const token = await requestToken();
            if (!token) {
                Report.warning("DASTR", "Unable to retrieve access token", "Understood");
                return;
            }

            const response = await API.getDocumentByRecordId(
                HeaderFarms(token),
                recordId
            );
            setDocumentData(response.data || null);
        } catch (error) {
            console.error("getDocumentByRecordId", error);
            const messageError =
                error?.response?.data?.error?.[0]?.name || "Unable to load document";
            Report.warning("DASTR", messageError, "Understood", () => navigate("/dst/history"));
        } finally {
            Block.remove(".dastr-view__body");
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDetail();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recordId]);

    const handleDownload = () => {
        if (!documentData?.attachment) {
            Report.info("DASTR", "Attachment not available", "Understood");
            return;
        }

        const link = document.createElement("a");
        link.href = documentData.attachment;
        link.download = `${recordId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const onChangeCollapse = (keys) => {
        setActiveKeys(keys);
    };

    return (
        <Fragment>
            <Navbar title="Document Detail" subtitle="DASTR" back navigate="/dst/history" />
            <div className="container-fluid white-back-cr">
                {loading ? (
                    <div className="dastr-view__loading">
                        <Spin size="large" />
                    </div>
                ) : !documentData ? (
                    <Empty description="Document not found" className="mt-5" />
                ) : (
                    <div className="container p-3" style={{ alignSelf: "center" }}>
                        <div className="row">
                            <div className="col-md-12">
                                <Collapse
                                    className="collapse_sr"
                                    expandIconPosition={"end"}
                                    bordered={true}
                                    activeKey={activeKeys}
                                    onChange={onChangeCollapse}
                                >
                                    <Panel style={{ background: "#fce8b6" }} header={<b>Document Information</b>} key="1">
                                        <div className="row">
                                            <div className="col-md-12">
                                                <Descriptions
                                                    bordered
                                                    column={1}
                                                    size="default"
                                                    className="dastr-view__description"
                                                >
                                                    <Descriptions.Item label="Record ID">
                                                        {documentData.record_id || "-"}
                                                    </Descriptions.Item>
                                                    <Descriptions.Item label="Document Name">
                                                        {documentData.document_name || "-"}
                                                    </Descriptions.Item>
                                                    <Descriptions.Item label="Document ID">
                                                        {documentData.document_id || "-"}
                                                    </Descriptions.Item>
                                                    <Descriptions.Item label="Created by">
                                                        {documentData?.requestor?.fullname || "-"}
                                                    </Descriptions.Item>
                                                    <Descriptions.Item label="Department">
                                                        {documentData?.requestor?.organization_name || "-"}
                                                    </Descriptions.Item>
                                                    <Descriptions.Item label="Created Date">
                                                        {documentData.created_at
                                                            ? moment(documentData.created_at).format("LLL")
                                                            : "-"}
                                                    </Descriptions.Item>
                                                    <Descriptions.Item label="Status">
                                                        {statusInfo ? (
                                                            <Tag color={statusInfo.color}>{statusInfo.label}</Tag>
                                                        ) : (
                                                            "-"
                                                        )}
                                                    </Descriptions.Item>
                                                    <Descriptions.Item label="Description">
                                                        {documentData.description || "-"}
                                                    </Descriptions.Item>
                                                </Descriptions>
                                            </div>
                                        </div>
                                    </Panel>

                                    <ApprovalInfo
                                        documentData={documentData}
                                        activeKeys={activeKeys}
                                        onChangeCollapse={onChangeCollapse}
                                    />

                                    {documentData.attachment && (
                                        <Panel style={{ background: "#e6f7ff" }} header={<b>Attachment</b>} key="3">
                                            <div className="dastr-view__attachment-header">
                                                <Button
                                                    icon={<DownloadOutlined />}
                                                    type="primary"
                                                    ghost
                                                    onClick={handleDownload}
                                                >
                                                    Download PDF
                                                </Button>
                                            </div>
                                            <PdfViewer fileUrl={documentData.attachment} height={480} />
                                        </Panel>
                                    )}
                                </Collapse>
                                <ApprovalActions mainData={documentData} />
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <Footer />
        </Fragment>
    );
};

const scrollToComments = (ref) => {
    if (ref && ref.current) {
        ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
};

const ViewDASTRWrapper = () => {
    const [activeKeys, setActiveKeys] = React.useState(['1', '2', '3']);
    const commentsRef = React.useRef(null);

    const handleCommentClick = () => {
        setActiveKeys(['1']); // open the comments panel
        scrollToComments(commentsRef);
    };

    return (
        <ViewDASTR
            activeKeys={activeKeys}
            setActiveKeys={setActiveKeys}
            commentsRef={commentsRef}
            onCommentClick={handleCommentClick}
        />
    );
};

export default ViewDASTRWrapper;

